import {
  collection,
  deleteDoc,
  doc,
  getDocs,
  onSnapshot,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { deleteObject, getBytes, getDownloadURL, ref, uploadBytesResumable } from "firebase/storage";
import { db, functions, storage } from "../services/firebase";
import { ORDEN_PIEZAS } from "./constants";
import { getExtension, sanitizeFileName, saveBytesAs } from "./files";
import { toDate } from "./format";
import { assertCanSend } from "./limits";

// Configuración de trabajo de una pieza dental.
export const EMPTY_PIEZA = {
  tipoTrabajo: "",
  otroTipoTrabajo: "",
  material: "",
  otroMaterial: "",
  color: "",
  otroColor: "",
  sobreImplante: false,
  marcaImplante: "",
  conexion: "",
  fijacion: "",
};

export const EMPTY_ORDER_FORM = {
  paciente: { nombre: "", edad: "", genero: "" },
  // piezas: { [número FDI]: configuración de EMPTY_PIEZA }
  trabajo: { piezas: {}, ferulizadas: "" },
  entrega: { fecha: "", prioridad: "Normal" },
  mensaje: "",
};

export function sortPiezas(numeros) {
  return [...numeros].map(Number).sort((a, b) => ORDEN_PIEZAS.indexOf(a) - ORDEN_PIEZAS.indexOf(b));
}

// Devuelve un objeto { "seccion.campo": "mensaje" } con los errores del formulario.
// Los errores de cada pieza usan la clave "pieza.<número>.<campo>".
export function validateOrderForm(form, modo, { recipient, files }) {
  const errors = {};
  if (!recipient) errors.recipient = "Elegí a quién le enviás.";
  if (modo === "archivos") {
    if (files.length === 0) errors.files = "Agregá al menos un archivo.";
    return errors;
  }
  const { paciente, trabajo, entrega } = form;
  // Paciente o referencia: un nombre o un código interno de la clínica.
  if (!paciente.nombre.trim()) errors["paciente.nombre"] = "Ingresá el paciente o una referencia.";
  if (paciente.edad && (Number(paciente.edad) < 0 || Number(paciente.edad) > 120)) {
    errors["paciente.edad"] = "Edad no válida.";
  }
  const numeros = sortPiezas(Object.keys(trabajo.piezas));
  if (numeros.length === 0) errors["trabajo.piezas"] = "Marcá al menos una pieza en el odontograma.";
  for (const numero of numeros) {
    const pieza = trabajo.piezas[numero];
    if (!pieza.tipoTrabajo) errors[`pieza.${numero}.tipoTrabajo`] = "Elegí el tipo de trabajo.";
    else if (pieza.tipoTrabajo === "Otro" && !pieza.otroTipoTrabajo.trim()) {
      errors[`pieza.${numero}.otroTipoTrabajo`] = "Especificá el tipo de trabajo.";
    }
  }
  if (!entrega.fecha) errors["entrega.fecha"] = "Indicá la fecha de entrega.";
  return errors;
}

export function firstPiezaWithError(errors) {
  const key = Object.keys(errors).find((k) => k.startsWith("pieza."));
  return key ? Number(key.split(".")[1]) : null;
}

// Convierte el estado del formulario en los datos que se guardan en Firestore.
export function buildOrderData(form, modo) {
  const mensaje = form.mensaje.trim();
  if (modo === "archivos") {
    return { tipo: "archivos", paciente: null, trabajo: null, entrega: null, mensaje };
  }
  const { paciente, trabajo, entrega } = form;
  const pick = (value, otro) => (value === "Otro" ? otro.trim() : value);
  const piezas = sortPiezas(Object.keys(trabajo.piezas)).map((numero) => {
    const p = trabajo.piezas[numero];
    return {
      numero,
      tipoTrabajo: pick(p.tipoTrabajo, p.otroTipoTrabajo),
      material: pick(p.material, p.otroMaterial),
      color: pick(p.color, p.otroColor),
      sobreImplante: p.sobreImplante,
      marcaImplante: p.sobreImplante ? p.marcaImplante.trim() : "",
      conexion: p.sobreImplante ? p.conexion : "",
      fijacion: p.sobreImplante ? p.fijacion : "",
    };
  });
  return {
    tipo: "orden",
    paciente: {
      nombre: paciente.nombre.trim(),
      edad: paciente.edad === "" ? null : Number(paciente.edad),
      genero: paciente.genero,
    },
    trabajo: {
      piezas,
      cantidad: piezas.length,
      ferulizadas: piezas.length > 1 ? trabajo.ferulizadas : "",
    },
    entrega: { fecha: entrega.fecha, prioridad: entrega.prioridad },
    mensaje,
  };
}

export function uploadBytes(path, bytes, contentType, onBytes) {
  return new Promise((resolve, reject) => {
    // Sin Content-Encoding: si no, el navegador descomprimiría el .gz por su cuenta al descargarlo.
    const task = uploadBytesResumable(ref(storage, path), bytes, { contentType });
    task.on("state_changed", (snapshot) => onBytes(snapshot.bytesTransferred), reject, resolve);
  });
}

// Arma la entrada de Firestore de un archivo preparado y la lista de lo que hay que subir.
export function describePrepared(base, index, file, prepared) {
  const ext = getExtension(file.name);
  const name = `${index}_${sanitizeFileName(file.name)}`;
  const path = `${base}/${name}${prepared.compresion ? ".gz" : ""}`;
  const uploads = [
    { path, bytes: prepared.stored, contentType: prepared.compresion ? "application/gzip" : file.type || "application/octet-stream" },
  ];

  let vistaPrevia = null;
  if (prepared.preview?.usarOriginal) {
    // El modelo ya es liviano: el visor usa el mismo archivo.
    vistaPrevia = { path, compresion: prepared.compresion, formato: ext, original: true, triangulos: prepared.preview.triangulos };
  } else if (prepared.preview) {
    // Mismo nivel de carpeta que el original: storage.rules solo admite ordenes/{uid}/{orden}/{archivo}.
    const previewPath = `${base}/${name}.preview.ply${prepared.preview.compresion ? ".gz" : ""}`;
    uploads.push({
      path: previewPath,
      bytes: prepared.preview.stored,
      contentType: prepared.preview.compresion ? "application/gzip" : "application/octet-stream",
    });
    vistaPrevia = {
      path: previewPath,
      compresion: prepared.preview.compresion,
      formato: "ply",
      original: false,
      triangulos: prepared.preview.triangulos,
      tamano: prepared.preview.stored.byteLength,
    };
  }

  const archivo = {
    nombre: file.name,
    tipo: file.type || "",
    formato: ext,
    tamano: file.size,
    tamanoComprimido: prepared.stored.byteLength,
    tamanoEntregado: prepared.tamanoEntregado,
    compresion: prepared.compresion,
    convertido: prepared.convertido,
    sha256: prepared.sha256,
    path,
    vistaPrevia,
  };
  return { archivo, uploads };
}

// Paths de Storage de un archivo de la orden (original y vista previa, si es aparte).
function storagePaths(file) {
  const paths = [file.path];
  if (file.vistaPrevia && !file.vistaPrevia.original) paths.push(file.vistaPrevia.path);
  return paths;
}

// onPrepare(actual, total, ahorro?) informa la preparación; ahorro llega al terminar.
// recipient: un contacto ({ uid, ... }) o un mail exacto ({ email, porMail: true }).
export async function createOrder({ sender, recipient, data, files, onProgress, onPrepare }) {
  const orderRef = doc(collection(db, "ordenes"));
  const base = `ordenes/${sender.uid}/${orderRef.id}`;
  const uploadedPaths = [];

  try {
    // Límites del plan: si ya no quedan envíos hoy, se avisa antes de comprimir.
    await assertCanSend(sender.uid, 0);
    const { runFileTask } = await import("./fileWorker");
    const described = [];
    for (const [index, file] of files.entries()) {
      onPrepare?.(index, files.length);
      described.push(describePrepared(base, index, file, await runFileTask("prepare", { file })));
    }
    const ahorro = {
      original: files.reduce((sum, file) => sum + file.size, 0),
      comprimido: described.reduce((sum, d) => sum + d.archivo.tamanoComprimido, 0),
    };
    onPrepare?.(files.length, files.length, ahorro);

    const uploads = described.flatMap((d) => d.uploads);
    // Lo que ocupa el envío ya comprimido (archivos y vistas previas): lo mismo que mide el servidor.
    const stored = uploads.reduce((sum, u) => sum + u.bytes.byteLength, 0);
    await assertCanSend(sender.uid, stored);
    const totalBytes = stored || 1;
    const transferred = new Map();
    await Promise.all(
      uploads.map((u) =>
        uploadBytes(u.path, u.bytes, u.contentType, (bytes) => {
          transferred.set(u.path, bytes);
          const sent = [...transferred.values()].reduce((a, b) => a + b, 0);
          onProgress?.(Math.round((sent / totalBytes) * 100));
        }).then(() => uploadedPaths.push(u.path))
      )
    );

    const archivos = described.map((d) => d.archivo);
    if (recipient.porMail) {
      // A un mail exacto: la orden la crea el servidor (busca si el mail tiene cuenta, sin decírselo a la app).
      const { sendToEmail } = await import("./connections");
      const { mensaje } = await sendToEmail({ email: recipient.email, ordenId: orderRef.id, datos: data, archivos });
      return { id: orderRef.id, ahorro, mensaje };
    }

    // A un contacto: la orden la crea el servidor, que valida los límites del plan (la app ya no puede crearla).
    await httpsCallable(functions, "crearOrden")({ destinatarioUid: recipient.uid, ordenId: orderRef.id, datos: data, archivos });
    return { id: orderRef.id, ahorro };
  } catch (error) {
    // Si algo falló, no dejamos archivos huérfanos en Storage.
    await Promise.allSettled(uploadedPaths.map((path) => deleteObject(ref(storage, path))));
    throw error;
  }
}

export function updateOrderStatus(orderId, estado) {
  return updateDoc(doc(db, "ordenes", orderId), { estado, actualizadoEn: serverTimestamp() });
}

export async function deleteOrder(order) {
  const comments = await getDocs(collection(db, "ordenes", order.id, "comentarios"));
  await Promise.all(comments.docs.map((comment) => deleteDoc(comment.ref)));
  const paths = (order.archivos || []).flatMap(storagePaths);
  await Promise.allSettled(paths.map((path) => deleteObject(ref(storage, path))));
  await deleteDoc(doc(db, "ordenes", order.id));
}

export function getFileUrl(path) {
  return getDownloadURL(ref(storage, path));
}

export function getFileBytes(path) {
  return getBytes(ref(storage, path));
}

// Los archivos subidos antes de la compresión no tienen el campo `compresion`.
export function isProcessedFile(file) {
  return "compresion" in file;
}

// URL firmada (10 minutos) de un archivo para quien está en el plan gratis: storage.rules no le deja leerlo directo.
// modo "descarga" cuenta una descarga; modo "vista" (visor 3D) no cuenta.
// version: null para los archivos de la orden, o el número de una versión del diseño.
export async function prepareDownload(orderId, index, modo = "descarga", version = null) {
  return (await httpsCallable(functions, "prepararDescarga")({ ordenId: orderId, archivo: index, modo, version })).data;
}

const isDenied = (error) => error?.code === "storage/unauthorized";

async function fetchBytes(url) {
  const response = await fetch(url);
  if (!response.ok) throw Object.assign(new Error(`HTTP ${response.status}`), { code: "storage/unknown" });
  return response.arrayBuffer();
}

// Bytes de un archivo de la orden: directo si storage.rules lo permite (tengo un plan pago, o soy el emisor);
// si no, con la URL firmada. Devuelve { buffer, restantes, max } (restantes: descargas que quedan, o null).
export async function getOrderFileBytes(orderId, index, path, modo = "descarga", version = null) {
  try {
    return { buffer: await getFileBytes(path), restantes: null, max: null };
  } catch (error) {
    if (!isDenied(error)) throw error;
    const { url, restantes, max } = await prepareDownload(orderId, index, modo, version);
    return { buffer: await fetchBytes(url), restantes, max };
  }
}

// URL para abrir un archivo viejo (anterior a la compresión) en otra pestaña.
export async function getOrderFileUrl(orderId, index, path, modo = "descarga", version = null) {
  try {
    return { url: await getFileUrl(path), restantes: null, max: null };
  } catch (error) {
    if (!isDenied(error)) throw error;
    return prepareDownload(orderId, index, modo, version);
  }
}

// Descarga, descomprime y verifica el archivo; lo entrega con su nombre original.
// Devuelve { restantes, max } si el archivo tiene límite de descargas.
export async function downloadOrderFile(orderId, index, file, version = null) {
  const [{ runFileTask }, { buffer, restantes, max }] = await Promise.all([import("./fileWorker"), getOrderFileBytes(orderId, index, file.path, "descarga", version)]);
  const bytes = await runFileTask("restore", { buffer, compresion: file.compresion, sha256: file.sha256 });
  saveBytesAs(bytes, file.nombre, file.tipo);
  return { restantes, max };
}

function fromSnapshot(snapshot) {
  return { id: snapshot.id, ...snapshot.data({ serverTimestamps: "estimate" }) };
}

export function subscribeToOrders(uid, onData, onError) {
  const q = query(collection(db, "ordenes"), where("participantes", "array-contains", uid));
  return onSnapshot(
    q,
    (snapshot) => {
      const orders = snapshot.docs.map(fromSnapshot);
      orders.sort((a, b) => (toDate(b.creadoEn)?.getTime() || 0) - (toDate(a.creadoEn)?.getTime() || 0));
      onData(orders);
    },
    onError
  );
}

export function subscribeToOrder(orderId, onData, onError) {
  return onSnapshot(
    doc(db, "ordenes", orderId),
    (snapshot) => onData(snapshot.exists() ? fromSnapshot(snapshot) : null),
    onError
  );
}

export function tiposDeTrabajo(order) {
  const piezas = order.trabajo?.piezas;
  if (!Array.isArray(piezas)) return order.trabajo?.tipoTrabajo ? [order.trabajo.tipoTrabajo] : [];
  return [...new Set(piezas.map((p) => p.tipoTrabajo).filter(Boolean))];
}

export function orderTitle(order) {
  if (order.tipo === "archivos") return "Envío de archivos";
  const tipos = tiposDeTrabajo(order);
  if (tipos.length === 0) return "Orden de trabajo";
  if (tipos.length <= 2) return tipos.join(" + ");
  return `${tipos.length} tipos de trabajo`;
}

export function piezasLabel(order) {
  const piezas = order.trabajo?.piezas;
  if (!Array.isArray(piezas) || piezas.length === 0) return "";
  return `${piezas.length === 1 ? "Pieza" : "Piezas"} ${piezas.map((p) => p.numero).join(", ")}`;
}
